import * as React from 'react'
import { format } from 'date-fns'
import { DateRange } from 'react-day-picker'
import { cn } from '@/lib/utils.ts'
import { Button } from '@/app/common/components/button/Button'
import { Calendar } from '@/shad/ui/calendar.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/shad/ui/popover.tsx'
import { CalendarIcon } from '@/common/icons/general.tsx'

type DatePickerWithRangeProps = React.HTMLAttributes<HTMLDivElement> & {
  value?: DateRange
  onDateChange: (date: DateRange | undefined) => void
  clearable?: boolean
  label?: string
}

export default function DatePickerWithRange({
  className,
  value,
  onDateChange,
  clearable = true,
  label = 'Pick a date',
}: DatePickerWithRangeProps) {
  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <div className="relative w-72">
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant="outline"
              className={cn(
                'flex w-full justify-center items-center text-left',
                value && clearable && 'pr-8',
                !value && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 text-lg" />

              <span>
                {value?.from ? (
                  value.to ? (
                    <>
                      {format(value.from, 'LLL dd, y')} -{' '}
                      {format(value.to, 'LLL dd, y')}
                    </>
                  ) : (
                    format(value.from, 'LLL dd, y')
                  )
                ) : (
                  label
                )}
              </span>
            </Button>
          </PopoverTrigger>

          {value && clearable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Clear date range"
              className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 p-0 text-lg font-semibold leading-none text-gray-400 hover:text-gray-700"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onDateChange(undefined)
              }}
            >
              ×
            </Button>
          )}
        </div>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={onDateChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
